import { DEFAULT_HISTORY_LIMITS } from "../history/historyTypes";
import type { IntegerRect } from "./selectionTypes";

// One command may use at most one third of the normal 3 MB history ceiling.
// That keeps the newest transform undoable while allowing several transforms
// before older commands need to be folded into the layer baseline.
export const MAX_SELECTION_REGION_BUFFER_BYTES =
  Math.floor(DEFAULT_HISTORY_LIMITS.maxEstimatedBytes / 3 / 4) * 4;
export const MAX_SELECTION_PIXELS = MAX_SELECTION_REGION_BUFFER_BYTES / 4;

// The only region-sized temporary surface is the source canvas used to draw a
// captured buffer. Full-document live previews are governed separately by the
// shared layer-memory budget.
export const MAX_SELECTION_TEMP_SURFACE_BYTES = MAX_SELECTION_REGION_BUFFER_BYTES;
export const SELECTION_OWNED_REGION_BUFFER_COUNT = 1;
export const SELECTION_REGION_CANVAS_SURFACE_COUNT = 1;
export const SELECTION_SIMULTANEOUS_REGION_ALLOCATION_COUNT =
  SELECTION_OWNED_REGION_BUFFER_COUNT + SELECTION_REGION_CANVAS_SURFACE_COUNT;
export const MAX_SELECTION_LIVE_REGION_BYTES =
  MAX_SELECTION_REGION_BUFFER_BYTES * SELECTION_SIMULTANEOUS_REGION_ALLOCATION_COUNT;
export const RASTER_SELECTION_COMMAND_OVERHEAD_BYTES = 320;

export function selectionPixelCount(rect: Readonly<IntegerRect>): number {
  if (
    !Number.isSafeInteger(rect.x) ||
    !Number.isSafeInteger(rect.y) ||
    !Number.isSafeInteger(rect.width) ||
    !Number.isSafeInteger(rect.height) ||
    rect.width < 1 ||
    rect.height < 1
  ) {
    return Number.POSITIVE_INFINITY;
  }
  const pixels = rect.width * rect.height;
  return Number.isSafeInteger(pixels) ? pixels : Number.POSITIVE_INFINITY;
}

export function selectionRegionBytes(rect: Readonly<IntegerRect>): number {
  const pixels = selectionPixelCount(rect);
  const bytes = pixels * 4;
  return Number.isSafeInteger(bytes) ? bytes : Number.POSITIVE_INFINITY;
}

export function selectionFitsMemoryLimits(rect: Readonly<IntegerRect>): boolean {
  const pixels = selectionPixelCount(rect);
  const bytes = selectionRegionBytes(rect);
  return (
    Number.isFinite(pixels) &&
    pixels <= MAX_SELECTION_PIXELS &&
    Number.isFinite(bytes) &&
    bytes <= MAX_SELECTION_REGION_BUFFER_BYTES &&
    bytes <= MAX_SELECTION_TEMP_SURFACE_BYTES
  );
}

export function selectionLiveRegionBytes(regionBytes: number): number {
  if (
    !Number.isSafeInteger(regionBytes) ||
    regionBytes < 0 ||
    regionBytes > MAX_SELECTION_REGION_BUFFER_BYTES
  ) {
    return Number.POSITIVE_INFINITY;
  }
  const liveBytes = regionBytes * SELECTION_SIMULTANEOUS_REGION_ALLOCATION_COUNT;
  return Number.isSafeInteger(liveBytes) && liveBytes <= MAX_SELECTION_LIVE_REGION_BYTES
    ? liveBytes
    : Number.POSITIVE_INFINITY;
}

export function estimateRasterSelectionCommandBytes(pixelBytes: number): number {
  if (!Number.isSafeInteger(pixelBytes) || pixelBytes < 0) return Number.POSITIVE_INFINITY;
  return RASTER_SELECTION_COMMAND_OVERHEAD_BYTES + pixelBytes;
}
